// Shared reply-template rendering.
//
// This existed twice, and the two copies had diverged into a live rendering
// bug. `ReplyComposer` learned that a template body may already BE HTML —
// `SaveAsTemplateDialog` stores the composer's raw `bodyHtml`, so most
// templates today contain `<p>`, `<br>`, `<strong>` — and passing that through
// an escaper turns it into visible markup. Its comment records the symptom:
// "`<p>Hi</p>` rendered as the literal text `&lt;p&gt;Hi&lt;/p&gt;` in the
// composer. That's the 'format messed up / markup visible' symptom users were
// seeing."
//
// `ComposeDialog` never got that fix and still ran every template through
// `plainToHtmlParagraphs`, so inserting a template into a brand-new outbound
// email showed raw tags to the agent — and shipped them to the client.
//
// One implementation now serves both.

export interface TemplateVars {
  /** Recipient's first name; falls back to "there". */
  firstName?: string | null;
  ticketSubject?: string | null;
  senderEmail?: string | null;
  /** First 8 chars of the ticket id. Empty on a compose (no ticket yet). */
  ticketIdShort?: string | null;
}

/** Best-effort first name from a display name or a bare email address. */
export function firstNameFrom(
  displayName?: string | null,
  email?: string | null,
): string {
  const fromName = displayName?.trim().split(/\s+/).filter(Boolean)[0];
  if (fromName) return fromName;
  // "jane.doe@acme.com" → "Jane". Better than "there" when we only have an
  // address, and it's what the recipient is used to seeing from us.
  const local = email?.split('@')[0]?.trim();
  if (local) {
    const first = local.split(/[._-]+/).filter(Boolean)[0];
    if (first && /^[a-z]+$/i.test(first)) {
      return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
    }
  }
  return 'there';
}

/** Substitute the `{{...}}` placeholders documented on /templates. */
export function applyTemplateVariables(body: string, vars: TemplateVars): string {
  return body
    .replace(/\{\{first_name\}\}/g, vars.firstName?.trim() || 'there')
    .replace(/\{\{ticket_subject\}\}/g, vars.ticketSubject ?? '')
    .replace(/\{\{sender_email\}\}/g, vars.senderEmail ?? '')
    .replace(/\{\{ticket_id_short\}\}/g, vars.ticketIdShort ?? '');
}

/** True when the string already carries markup and must NOT be escaped. */
export function looksLikeHtml(body: string): boolean {
  return /<\w+[^>]*>/.test(body);
}

/** Wrap plain text as HTML paragraphs, preserving blank-line paragraph breaks
 *  and soft line breaks. Escapes, so only ever call it on genuine plain text.
 *
 *  Paragraphs are separated by an explicit blank row (`<p><br></p>`), not left
 *  touching. The composer renders `<p>` with zero margin, so touching paragraphs
 *  looked cramped there — and the agent fixed it by pressing Enter, which then
 *  doubled up with the margins mail clients add. A real blank row is what the
 *  agent sees, and `prepareOutboundHtml` sends it as-is. */
export function plainToHtmlParagraphs(plain: string): string {
  if (!plain) return '';
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return plain
    .replace(/\r\n/g, '\n')
    .split(/\n\s*\n+/)
    .map((p) => `<p>${escape(p).replace(/\n/g, '<br>')}</p>`)
    .join('<p><br></p>');
}

/** The one entry point call sites should use: fill variables, then pass HTML
 *  through verbatim or wrap plain text in paragraphs. */
export function renderTemplateForInsert(body: string, vars: TemplateVars): string {
  const filled = applyTemplateVariables(body, vars);
  return looksLikeHtml(filled) ? filled : plainToHtmlParagraphs(filled);
}
